import { styled } from "@linaria/react"
import { useMatches, useNavigate, useParams } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useEffect } from "react"

import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Horizontal } from "@/components/Flex.ts"
import { ProjectCodingGuidelinesTab } from "@/components/ProjectSettingsDialog/ProjectCodingGuidelinesTab.tsx"
import { ProjectGeneralTab } from "@/components/ProjectSettingsDialog/ProjectGeneralTab.tsx"
import { ProjectMirroringTab } from "@/components/ProjectSettingsDialog/ProjectMirroringTab.tsx"
import { ProjectPooledPreviewTab } from "@/components/ProjectSettingsDialog/ProjectPooledPreviewTab.tsx"
import { ProjectPreviewSetupTab } from "@/components/ProjectSettingsDialog/ProjectPreviewSetupTab.tsx"
import { ProjectRootFolderTab } from "@/components/ProjectSettingsDialog/ProjectRootFolderTab.tsx"
import { ProjectSecretsTab } from "@/components/ProjectSettingsDialog/ProjectSecretsTab.tsx"
import { ProjectSettingsDialogModel } from "@/components/ProjectSettingsDialog/ProjectSettingsDialogModel.ts"
import { TabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import { ProjectWarmupSnapshotsTab } from "@/components/ProjectSettingsDialog/ProjectWarmupSnapshotsTab.tsx"
import { SettingsDialogStyles } from "@/components/ProjectSettingsDialog/SettingsDialogStyles.ts"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import { AlertFill, Icon16 } from "@/icons/Icons.ts"
import { Navigator } from "@/routes/Navigator.ts"

import type { ProjectSettingsTabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import type { PR_Project2 } from "@/lib/client/generated/TypeDefinitions.ts"

export const ProjectSettingsDialog = ({ project, dialogOpen, setDialogOpen, initialTab }: {
  project: PR_Project2
  dialogOpen: boolean
  setDialogOpen: (open: boolean) => void
  initialTab?: ProjectSettingsTabId
}) => {

  return (
    <Dialog
      top="128px"
      maxWidth="870px"
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      trigger={null}
      title={<DialogTitle>Project Settings</DialogTitle>}
    >
      <SettingsDialogContent project={project} initialTab={initialTab} />
    </Dialog>
  )
}

const SettingsDialogContent = observer(({ project, initialTab }: {
  project: PR_Project2
  initialTab?: ProjectSettingsTabId
}) => {
  const devMode = useDevMode()

  const navigate = useNavigate()
  const routeTab = useParams({ strict: false }).tab
  const isSettingsRoute = useMatches().some(it => it.routeId === "/$orgId/p/$projectKey/settings/$tab")

  const context = useOrgContext()
  const orgId = context.orgId

  const effectiveInitialTab = initialTab ?? routeTab

  const model = useModel(() => new ProjectSettingsDialogModel(project, effectiveInitialTab, { context }), [project, effectiveInitialTab])

  const currentTab = model.currentTab.get()

  useEffect(() => {
    if (isSettingsRoute) {
      void navigate(Navigator.projectSettings(orgId, project.key, currentTab))
    }
  }, [isSettingsRoute, navigate, orgId, project.key, currentTab])

  return (
    <SettingsDialogStyles.Layout>
      <SettingsDialogStyles.Sidebar>
        <Scrollable childrenContainerClassName={SettingsDialogStyles.SidebarInner}>
          <SettingsDialogStyles.SidebarTitle>Project</SettingsDialogStyles.SidebarTitle>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.General}>
            <SettingsDialogStyles.SidebarLink
              {...Navigator.projectSettings(orgId, project.key, TabId.General)}
              onClick={model.onTabClick(TabId.General)}
              data-testid="project-settings-sideitem-general"
            >
              General
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.CodingGuidelines}>
            <SettingsDialogStyles.SidebarLink
              {...Navigator.projectSettings(orgId, project.key, TabId.CodingGuidelines)}
              onClick={model.onTabClick(TabId.CodingGuidelines)}
              data-testid="project-settings-sideitem-coding-guidelines"
            >
              Coding Guidelines
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.Git}>
            <SettingsDialogStyles.SidebarLink
              {...Navigator.projectSettings(orgId, project.key, TabId.Git)}
              onClick={model.onTabClick(TabId.Git)}
              data-testid="project-settings-sideitem-git"
            >
              <Horizontal gap="8px" alignItems="center">
                Git

                {model.repoSyncStatus.get()?.authError === true && (
                  <AlertFill className={Icon16} color="var(--error-color)" />
                )}
              </Horizontal>

            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.RootFolder}>
            <SettingsDialogStyles.SidebarLink
              {...Navigator.projectSettings(orgId, project.key, TabId.RootFolder)}
              onClick={model.onTabClick(TabId.RootFolder)}
              data-testid="project-settings-sideitem-root-folder"
            >
              Root Folder
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarSeparator />

          <SettingsDialogStyles.SidebarTitle>Preview</SettingsDialogStyles.SidebarTitle>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.PreviewSetup}>
            <SettingsDialogStyles.SidebarLink
              {...Navigator.projectSettings(orgId, project.key, TabId.PreviewSetup)}
              onClick={model.onTabClick(TabId.PreviewSetup)}
              data-testid="project-settings-sideitem-preview-setup"
            >
              Preview Setup
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.Secrets}>
            <SettingsDialogStyles.SidebarLink
              {...Navigator.projectSettings(orgId, project.key, TabId.Secrets)}
              onClick={model.onTabClick(TabId.Secrets)}
              data-testid="project-settings-sideitem-secrets"
            >
              Secrets
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.WarmupSnapshots}>
            <SettingsDialogStyles.SidebarLink
              {...Navigator.projectSettings(orgId, project.key, TabId.WarmupSnapshots)}
              onClick={model.onTabClick(TabId.WarmupSnapshots)}
              data-testid="project-settings-sideitem-warmup-snapshots"
            >
              Warmup Snapshots
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          {devMode && (
            <SettingsDialogStyles.SidebarItem active={currentTab == TabId.PooledPreview}>
              <SettingsDialogStyles.SidebarLink
                {...Navigator.projectSettings(orgId, project.key, TabId.PooledPreview)}
                onClick={model.onTabClick(TabId.PooledPreview)}
                data-testid="project-settings-sideitem-pooled-preview"
              >
                Pooled Preview
              </SettingsDialogStyles.SidebarLink>
            </SettingsDialogStyles.SidebarItem>
          )}
        </Scrollable>
      </SettingsDialogStyles.Sidebar>

      <SettingsDialogStyles.Separator />

      <SettingsDialogStyles.Content>
        <Scrollable childrenContainerClassName={SettingsDialogStyles.ContentInner}>
          {currentTab == TabId.General && (
            <ProjectGeneralTab model={model.generalTabModel} />
          )}

          {currentTab == TabId.CodingGuidelines && (
            <ProjectCodingGuidelinesTab model={model.codingGuidelinesTabModel} />
          )}

          {currentTab == TabId.Git && (
            <ProjectMirroringTab model={model.mirroringTabModel} />
          )}

          {currentTab == TabId.RootFolder && (
            <ProjectRootFolderTab model={model.rootFolderTabModel} />
          )}

          {currentTab == TabId.PreviewSetup && (
            <ProjectPreviewSetupTab model={model.previewSettingsTabModel} />
          )}

          {currentTab == TabId.Secrets && (
            <ProjectSecretsTab model={model.secretsTabModel} canEditProject={model.canViewProject} /> // Secrets management is available to members
          )}

          {currentTab == TabId.WarmupSnapshots && (
            <ProjectWarmupSnapshotsTab model={model.warmupSnapshotsTabModel} />
          )}

          {devMode && currentTab == TabId.PooledPreview && (
            <ProjectPooledPreviewTab model={model.pooledPreviewTabModel} />
          )}
        </Scrollable>
      </SettingsDialogStyles.Content>
    </SettingsDialogStyles.Layout>
  )
})

const DialogTitle = styled(SettingsDialogStyles.Title)`
  display: flex;
  align-items: center;
  padding-right: 20px;
`
